export function toLocalInputValue(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function isValidEventDateRange(
  startsAt: string,
  endsAt: string,
): boolean {
  if (!startsAt || !endsAt) return false;
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }
  return end.getTime() > start.getTime();
}

/** Same limit as the API (`@MaxLength(120)` on the event name, DEV-820). */
export const LEAD_EVENT_NAME_MAX = 120;

export const UNUSUALLY_LONG_EVENT_DAYS = 90;

export function eventRangeDayCount(
  startsAt: string,
  endsAt: string,
): number | null {
  if (!isValidEventDateRange(startsAt, endsAt)) return null;
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const startDay = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const dayMs = 24 * 60 * 60 * 1000;
  return Math.round((endDay.getTime() - startDay.getTime()) / dayMs);
}

export function isUnusuallyLongEventRange(
  startsAt: string,
  endsAt: string,
  maxDays = UNUSUALLY_LONG_EVENT_DAYS,
): boolean {
  const days = eventRangeDayCount(startsAt, endsAt);
  return days != null && days > maxDays;
}

export function formatEventDurationLabel(
  startsAt: string,
  endsAt: string,
  locale: string,
  labels: {
    durationSameDay: string;
    durationDays: string;
  },
): string | null {
  const dayDiff = eventRangeDayCount(startsAt, endsAt);
  if (dayDiff == null) return null;
  if (dayDiff <= 0) return labels.durationSameDay;
  return labels.durationDays.replace('{count}', String(dayDiff));
}

export function addDaysToLocalInputValue(
  startsAt: string,
  days: number,
  endHour = 18,
): string {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return '';
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  end.setHours(endHour, 0, 0, 0);
  return toLocalInputValue(end);
}

export type RosterPreviewTeam = {
  memberCount?: number;
  /** Loaded member user ids; without them the team's count is used. */
  memberUserIds?: readonly string[];
};

/**
 * "{total} Personen ({direct} direkt, {viaTeams} über Teams)": a person picked
 * directly and through a team, or through two teams, counts once (DEV-820).
 */
export function resolveRosterPreview(
  directUserIds: readonly string[],
  teams: readonly RosterPreviewTeam[],
): { total: number; direct: number; viaTeams: number } {
  const direct = new Set(directUserIds);
  const viaTeamIds = new Set<string>();
  let unresolvedCount = 0;
  for (const team of teams) {
    if (team.memberUserIds) {
      for (const id of team.memberUserIds) viaTeamIds.add(id);
    } else {
      unresolvedCount += team.memberCount ?? 0;
    }
  }
  const everyone = new Set([...direct, ...viaTeamIds]);
  return {
    total: everyone.size + unresolvedCount,
    direct: direct.size,
    viaTeams: viaTeamIds.size + unresolvedCount,
  };
}
